import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

export function exportExcel({
  fileName = "report.xlsx",
  tableData = [],
  tableColumns = [],
  tableHeader = [],
}) {
  const visibleColumns = tableColumns.filter((col) => col.visible !== false);
  const fields = visibleColumns.map((col) => col.field);

  if (!tableHeader || tableHeader.length === 0) {
    tableHeader = [
      visibleColumns.map((col) => ({
        header: col.header,
        colspan: 1,
        rowspan: 1,
      })),
    ];
  }

  const worksheet = {};
  const merges = []; // сюда собираются объединения ячеек
  const occupied = {}; // карта занятых ячеек (из-за rowspan/colspan)
  let currentRow = 0;

  // Находит следующий свободный столбец, если ячейка занята объединением
  function findFreeCol(row, col) {
    while (occupied[`${row}:${col}`]) col++;
    return col;
  }

  // Построение многоуровневых заголовков
  tableHeader.forEach((row) => {
    let colIndex = 0;

    row.forEach((cell) => {
      // Ищем куда можно поставить ячейку
      colIndex = findFreeCol(currentRow, colIndex);
      const addr = XLSX.utils.encode_cell({ r: currentRow, c: colIndex });

      worksheet[addr] = {
        t: "s",
        v: cell.header,
        s: {
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        },
      };

      const rowspan = cell.rowspan || 1;
      const colspan = cell.colspan || 1;

      // Если ячейка объединённая — регистрируем merge и отмечаем область как занятую
      if (rowspan > 1 || colspan > 1) {
        merges.push({
          s: { r: currentRow, c: colIndex },
          e: { r: currentRow + rowspan - 1, c: colIndex + colspan - 1 },
        });

        // помечаем все ячейки в merge как занятые
        for (let r = 0; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) {
            occupied[`${currentRow + r}:${colIndex + c}`] = true;
          }
        }
      } else {
        occupied[`${currentRow}:${colIndex}`] = true;
      }

      colIndex += colspan;
    });

    currentRow++;
  });

  worksheet["!merges"] = merges;

  const filteredData = tableData.map((item) =>
    Object.fromEntries(
      fields.map((k) => {
        let value = item[k];
        if (value === true) value = "ДА";
        else if (value === false) value = "НЕТ";
        return [k, value ?? ""];
      }),
    ),
  );

  // Преобразуем данные в ячейки Excel (без заголовков)
  const dataSheet = XLSX.utils.json_to_sheet(filteredData, {
    skipHeader: true,
    origin: currentRow,
  });
  Object.keys(dataSheet).forEach((key) => {
    if (key !== "!ref" && key !== "!cols") {
      worksheet[key] = dataSheet[key];
    }
  });

  // формируем область листа
  const dataRange = XLSX.utils.decode_range(dataSheet["!ref"]);
  const lastDataRow = dataRange.e.r - dataRange.s.r;
  worksheet["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastDataRow, c: fields.length - 1 },
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Данные");

  // Проставляем границы по всей таблице
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[addr]) {
        worksheet[addr] = { t: "s", v: "" };
      }

      if (!worksheet[addr].s) worksheet[addr].s = {};

      worksheet[addr].s.border = {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      };
    }
  }

  // Автоширина колонок
  const headerTextsByCol = expandHeader(tableHeader);
  worksheet["!cols"] = fields.map((field, index) => {
    const headerLens = headerTextsByCol[index].map((t) => t.length);
    const dataLens = filteredData.map((r) =>
      r[field] ? r[field].toString().length : 0,
    );
    const maxLen = Math.max(...headerLens, ...dataLens);
    return { wch: Math.min(maxLen + 2, 50) };
  });

  // Генерируем и сохраняем файл Excel
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, fileName);
}

function expandHeader(tableHeader) {
  const grid = [];
  let maxCols = 0;

  tableHeader.forEach((row, rowIndex) => {
    grid[rowIndex] = grid[rowIndex] || [];

    let colIndex = 0;
    while (grid[rowIndex][colIndex]) colIndex++;

    // Заполняем область, занятую объединённой ячейкой
    row.forEach((cell) => {
      const colspan = cell.colspan || 1;
      const rowspan = cell.rowspan || 1;

      for (let r = 0; r < rowspan; r++) {
        for (let c = 0; c < colspan; c++) {
          const rr = rowIndex + r;
          const cc = colIndex + c;
          grid[rr] = grid[rr] || [];
          grid[rr][cc] = {
            header: cell.header,
            colspan,
          };
        }
      }

      colIndex += colspan;
      maxCols = Math.max(maxCols, colIndex);
    });
  });

  const headersByCol = Array.from({ length: maxCols }, () => []);

  // Проходим по развернутой матрице и собираем заголовки,
  // учитываем только ячейки без colspan (то есть конечные колонки)
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < grid[r].length; c++) {
      const cell = grid[r][c];
      if (cell && cell.colspan === 1) {
        headersByCol[c].push(cell.header);
      }
    }
  }

  return headersByCol; // список заголовков по каждой конечной колонке
}